import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import Cookies from 'js-cookie'
import config from 'app/config/config'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'

/**
 * Mirrors the backend event in
 * `app-modules/signable-document/document-refresh-events.ts`. `reason` is a
 * closed list produced there and is rendered verbatim — the frontend never
 * derives its own wording from an error.
 */
type DocumentRefreshFailureEvent = {
  type: 'document-refresh-failed'
  documentLabel: string
  operationName: string
  reason: string
  subTradeIds: string[]
  facilityId?: string
  occurredAt: string
}

/**
 * TEMPORARY (testing scaffold) — the backend also emits a success twin so the
 * stream can be exercised without inducing a real failure. Remove this type,
 * `isSuccessEvent`, and the success branch in `handleFrame` together with the
 * backend's `DocumentRefreshSuccessEvent`.
 */
type DocumentRefreshSuccessEvent = {
  type: 'document-refresh-succeeded'
  documentLabel: string
  operationName: string
  subTradeIds: string[]
  facilityId?: string
  occurredAt: string
}

const RECONNECT_DELAY_MS = 10000
const NOTIFICATION_DURATION_MS = 12000

const isFailureEvent = (value: unknown): value is DocumentRefreshFailureEvent =>
  !!value &&
  typeof value === 'object' &&
  (value as DocumentRefreshFailureEvent).type === 'document-refresh-failed'

/** TEMPORARY (testing scaffold) — see DocumentRefreshSuccessEvent. */
const isSuccessEvent = (value: unknown): value is DocumentRefreshSuccessEvent =>
  !!value &&
  typeof value === 'object' &&
  (value as DocumentRefreshSuccessEvent).type === 'document-refresh-succeeded'

const tradeScope = (subTradeIds: string[] | undefined) => {
  const count = subTradeIds?.length ?? 0
  return count === 1 ? ' for 1 trade' : count > 1 ? ` for ${count} trades` : ''
}

const failureMessage = (event: DocumentRefreshFailureEvent) =>
  `${event.documentLabel} could not be refreshed${tradeScope(event.subTradeIds)} after the ${event.operationName} — ${event.reason}. ` +
  'The amounts shown are out of date until the documents are regenerated.'

/** TEMPORARY (testing scaffold) — see DocumentRefreshSuccessEvent. */
const successMessage = (event: DocumentRefreshSuccessEvent) =>
  `${event.documentLabel} refreshed${tradeScope(event.subTradeIds)} after the ${event.operationName}.`

/**
 * Surfaces background document-refresh failures for the whole session.
 *
 * Regeneration after an amount change (paydown, amendment, PIK, rollover, ...)
 * runs detached from the request that triggered it, so its failure cannot come
 * back on that request's response — the user has already been told the action
 * succeeded. The backend streams those failures over SSE instead; this hook
 * listens for the life of the session and raises a toast wherever the user
 * happens to be.
 *
 * The stream is already scoped server-side to the signed-in user — the backend
 * filters on the actor it captured from the triggering request's JWT, so events
 * for other people's actions never reach this browser. No client-side filter is
 * needed, and adding one would not make it safer.
 *
 * `EventSource` cannot carry an Authorization header, so the stream is read
 * with `fetch` — the same approach as the allocation heartbeat.
 */
export const useDocumentRefreshFailures = () => {
  const { isAuthenticated } = useAuth0()

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    const controller = new AbortController()
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let stopped = false

    const scheduleReconnect = () => {
      if (stopped) {
        return
      }
      reconnectTimer = setTimeout(consume, RECONNECT_DELAY_MS)
    }

    // One SSE frame is `data: {...}` lines terminated by a blank line.
    const handleFrame = (frame: string) => {
      const data = frame
        .split('\n')
        .filter(line => line.startsWith('data:'))
        .map(line => line.slice(5).trim())
        .join('')
      if (!data) {
        return
      }
      try {
        const parsed = JSON.parse(data)
        if (isFailureEvent(parsed)) {
          ErrorNotification({
            title: 'Documents not refreshed',
            message: failureMessage(parsed),
            duration: NOTIFICATION_DURATION_MS,
          })
          return
        }
        // TEMPORARY (testing scaffold) — remove with the success event.
        if (isSuccessEvent(parsed)) {
          SuccessNotification({
            title: 'Documents refreshed',
            message: successMessage(parsed),
          })
        }
      } catch {
        // A frame we cannot parse is not worth interrupting the user for.
      }
    }

    async function consume() {
      const token = Cookies.get('access_token')
      if (!token) {
        scheduleReconnect()
        return
      }

      try {
        const response = await fetch(
          `${config.backendUrl}/api/signable-documents/document-refresh-events`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'text/event-stream',
            },
            signal: controller.signal,
          },
        )

        if (!response.ok || !response.body) {
          scheduleReconnect()
          return
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        for (;;) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          // Keep the trailing partial frame in the buffer until it completes.
          const frames = buffer.split('\n\n')
          buffer = frames.pop() ?? ''
          frames.forEach(handleFrame)
        }

        // The server closed the stream (deploy, idle timeout) — reopen it, or
        // failures land in a void for the rest of the session.
        scheduleReconnect()
      } catch {
        if (!controller.signal.aborted) {
          scheduleReconnect()
        }
      }
    }

    consume()

    return () => {
      stopped = true
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
      }
      controller.abort()
    }
  }, [isAuthenticated])
}
